import React, { useEffect, useMemo, useRef } from 'react';
import { Animated, Easing, Image, Platform, Pressable, Text, View } from 'react-native';
import Svg, { Defs, Ellipse, RadialGradient, Stop } from 'react-native-svg';
import { ChevronRight } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { getAssetUrl, ASSET_PATHS } from '../../../constants/assets';
import { WelcomeDisclaimerFooter } from '../../worldcup/WorldCupWelcomeScreen/WelcomeDisclaimerFooter';
import type { CompetitionIntroConfig } from '../../../src/competitions/types/competitionIntro.types';
import {
  COMPETITION_WELCOME_ANIM,
  COMPETITION_WELCOME_CIRCLE_COUNT,
} from './CompetitionWelcomeScreen.constants';
import {
  getCompetitionWelcomeCircleStyles,
  getCompetitionWelcomeSplashStyles,
} from './CompetitionWelcomeScreen.styles';

/** CDN, never a bundled require() — see .cursor/rules/assets-cdn.mdc. */
const MASCOT_URI = getAssetUrl(ASSET_PATHS.AVATARS.monkey.happy);

const GLOW_WIDTH = 240;
const GLOW_HEIGHT = 190;

export interface CompetitionWelcomeSplashPhaseProps {
  content: CompetitionIntroConfig;
  title: string;
  isExiting: boolean;
  topInset: number;
  bottomInset: number;
  phaseIndex: number;
  phaseCount: number;
  onNext: () => void;
  testID?: string;
  /** Stable across phases so a walkthrough can click "next" without knowing the beat. */
  ctaTestID?: string;
}

/**
 * Splash beat: mascot under a glow dome with pulsing rings, then the competition
 * name, tagline and CTA. The World Cup splash uses host flags and a trophy icon —
 * a generic competition has neither, so the app mascot carries the hero instead.
 */
export const CompetitionWelcomeSplashPhase: React.FC<CompetitionWelcomeSplashPhaseProps> = ({
  content,
  title,
  isExiting,
  topInset,
  bottomInset,
  phaseIndex,
  phaseCount,
  onNext,
  testID,
  ctaTestID,
}) => {
  const theme = useTheme();
  const styles = useMemo(
    () => getCompetitionWelcomeSplashStyles(theme, { topInset, bottomInset }),
    [theme, topInset, bottomInset],
  );
  const circleStyles = useMemo(() => getCompetitionWelcomeCircleStyles(theme), [theme]);

  const circleAnims = useRef(
    Array.from({ length: COMPETITION_WELCOME_CIRCLE_COUNT }, () => new Animated.Value(0)),
  ).current;
  const bob = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const anims: Animated.CompositeAnimation[] = [];
    /** Web: native-driver loops on transform/opacity are unreliable in RNW. */
    const useNativeDriver = Platform.OS !== 'web';

    circleAnims.forEach((anim, i) => {
      const duration =
        COMPETITION_WELCOME_ANIM.CIRCLE_BASE_DURATION_MS +
        i * COMPETITION_WELCOME_ANIM.CIRCLE_DURATION_STEP_MS;
      const t = setTimeout(() => {
        const loop = Animated.loop(
          Animated.sequence([
            Animated.timing(anim, {
              toValue: 1,
              duration,
              easing: Easing.inOut(Easing.sin),
              useNativeDriver,
            }),
            Animated.timing(anim, {
              toValue: 0,
              duration,
              easing: Easing.inOut(Easing.sin),
              useNativeDriver,
            }),
          ]),
        );
        loop.start();
        anims.push(loop);
      }, i * COMPETITION_WELCOME_ANIM.CIRCLE_DELAY_STEP_MS);
      timeouts.push(t);
    });

    const bobLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(bob, {
          toValue: 1,
          duration: COMPETITION_WELCOME_ANIM.MASCOT_BOB_MS / 2,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver,
        }),
        Animated.timing(bob, {
          toValue: 0,
          duration: COMPETITION_WELCOME_ANIM.MASCOT_BOB_MS / 2,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver,
        }),
      ]),
    );
    bobLoop.start();
    anims.push(bobLoop);

    return () => {
      timeouts.forEach(clearTimeout);
      anims.forEach((a) => a.stop());
    };
  }, [circleAnims, bob]);

  const mascotTranslateY = bob.interpolate({ inputRange: [0, 1], outputRange: [0, -8] });

  return (
    <View style={styles.container} testID={testID}>
      <View style={styles.heroWrap} testID={`${testID}-hero`}>
        <Svg width={GLOW_WIDTH} height={GLOW_HEIGHT} style={styles.glow} pointerEvents="none">
          <Defs>
            <RadialGradient id="competitionWelcomeGlow" cx="50%" cy="50%" r="50%">
              <Stop offset="0%" stopColor={theme.primaryLight} stopOpacity={0.45} />
              <Stop offset="100%" stopColor={theme.primaryLight} stopOpacity={0} />
            </RadialGradient>
          </Defs>
          <Ellipse
            cx={GLOW_WIDTH / 2}
            cy={GLOW_HEIGHT / 2}
            rx={GLOW_WIDTH / 2}
            ry={GLOW_HEIGHT / 2}
            fill="url(#competitionWelcomeGlow)"
          />
        </Svg>

        <View style={styles.circlesLayer} pointerEvents="none">
          {circleAnims.map((anim, i) => {
            const scale = anim.interpolate({ inputRange: [0, 1], outputRange: [1, 1.18] });
            const opacity = anim.interpolate({ inputRange: [0, 1], outputRange: [0.1, 0.28] });
            return (
              <Animated.View
                key={i}
                style={[circleStyles[i], { transform: [{ scale }], opacity }]}
              />
            );
          })}
        </View>

        <Animated.View style={{ transform: [{ translateY: mascotTranslateY }] }}>
          <Image
            source={{ uri: MASCOT_URI }}
            style={styles.mascot}
            accessibilityLabel="Zporti mascot"
          />
        </Animated.View>
      </View>

      <Text style={styles.eyebrow}>{content.splash.subtitle}</Text>
      <Text style={styles.title}>{title}</Text>
      <Text style={styles.tagline}>{content.splash.tagline}</Text>

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={content.splash.cta}
        disabled={isExiting}
        onPress={onNext}
        style={({ pressed, hovered }) => [
          styles.button,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && !isExiting && styles.buttonPressed,
          hovered && Platform.OS === 'web' && styles.buttonHovered,
        ]}
        testID={ctaTestID}
      >
        <Text style={styles.buttonLabel}>{content.splash.cta}</Text>
        <ChevronRight size={16} color={theme.onPrimary} strokeWidth={2.5} />
      </Pressable>

      <View style={styles.dots}>
        {Array.from({ length: phaseCount }, (_, i) => (
          <View key={i} style={[styles.dot, i === phaseIndex && styles.dotActive]} />
        ))}
      </View>

      <WelcomeDisclaimerFooter />
    </View>
  );
};
